// The top bar's breadcrumb (UAT plan D-2; SH-2 "← Requisitions › REQ-1044", CA-15): the first crumb is the navigation
// item the route belongs to, leading back to it when the route names a record; the second is that record — an R-id, an
// RQ-id, a candidate — until the view that draws the record names it better through useShellCrumbs() (the lanes'
// views call it as their screens land on the design system). A screen no navigation item lights draws its record
// alone, or no crumb: its own heading names it.

import { useEffect, useSyncExternalStore } from "react";
import type { NavItemOut } from "@/api/tools";
import type { TopBarCrumb } from "@/mc/g4a";
import type { Route } from "@/router";
import { activeItem } from "./nav";

/** A requisition (R-1040) or a Hiring Request (RQ-0008) named by the route. */
const RECORD_ID = /^RQ?-\d+$/;
/** A candidate's crumb until the profile names the person. */
const CANDIDATE = "Candidate";

/** The record the route names, as the route alone can say it. */
function recordCrumb(route: Route): TopBarCrumb | null {
  const [first, second] = route.parts;
  if (!first) return null;
  if (RECORD_ID.test(first)) return { label: first };
  if (route.view === "candidates" && (first.startsWith("cand_") || (first === "ref" && second))) return { label: CANDIDATE };
  return null;
}

/**
 * The crumbs for a route: the active navigation item (a link back when a record follows it), then the view's own crumbs
 * when a view has set them, else the record the route names.
 */
export function shellCrumbs(route: Route, items: readonly NavItemOut[], view: readonly TopBarCrumb[] | null): TopBarCrumb[] {
  const item = activeItem(items, route);
  const record = recordCrumb(route);
  const after = view && view.length > 0 ? [...view] : record ? [record] : [];
  if (!item) return after;
  if (after.length === 0) return [{ label: item.label }];
  return [{ label: item.label, href: item.route }, ...after];
}

// ---- the view's crumbs: one store the top bar reads, set by the view that is mounted -------------------------------
let current: readonly TopBarCrumb[] | null = null;
const listeners = new Set<() => void>();
const emit = () => { for (const listener of listeners) listener(); };

export function subscribeViewCrumbs(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function viewCrumbs(): readonly TopBarCrumb[] | null {
  return current;
}

/** Sets the crumbs after the navigation item; the returned function clears them if they are still the ones set. */
export function setViewCrumbs(crumbs: readonly TopBarCrumb[]): () => void {
  current = crumbs;
  emit();
  return () => {
    if (current !== crumbs) return;
    current = null;
    emit();
  };
}

/**
 * A view names its record in the top bar (its title, a person's name, a deeper page) while it is mounted:
 * `useShellCrumbs([{ label: candidate.name }])` reads "Candidates › Michael Johnson".
 */
export function useShellCrumbs(crumbs: readonly TopBarCrumb[] | null | undefined): void {
  const key = crumbs && crumbs.length > 0 ? JSON.stringify(crumbs) : "";
  useEffect(() => (key ? setViewCrumbs(JSON.parse(key) as TopBarCrumb[]) : undefined), [key]);
}

/** The crumbs the mounted view has set, or null. */
export function useViewCrumbs(): readonly TopBarCrumb[] | null {
  return useSyncExternalStore(subscribeViewCrumbs, viewCrumbs, () => null);
}
